import React from 'react';
import { Button, Typography } from 'antd';
import { FileTextOutlined, FolderOpenOutlined, MessageOutlined } from '@ant-design/icons';
import ProjectSearchInput from './ProjectSearchInput.jsx';
import ChatWorkspaceSourcesPanel from './ChatWorkspaceSourcesPanel.jsx';
import ProjectDetailSettingsMenu from './ProjectDetailSettingsMenu.jsx';
import ChatWorkspacePanelCoordinator from './ChatWorkspacePanelCoordinator.jsx';
import ProjectArtifactCollection from '../../session/ProjectArtifactCollection.jsx';
import SessionRows from '../../session/SessionRows.jsx';
import { getProjectId } from '../../session/sessionIdentity.js';
import { ChatWorkspaceConversationLayout } from '../layout/index.js';

const { Text } = Typography;
const count = (value) => (
  Number.isFinite(Number(value)) && Number(value) >= 0 ? Number(value) : 0
);
function ProjectCollectionTabs({ activeTab, artifactTabDisabled, onChange, resolvedUILabels }) {
  const items = [
    {
      disabled: false,
      icon: <MessageOutlined />,
      label: resolvedUILabels.project_conversations || '项目对话',
      value: 'project_conversations',
    },
    {
      disabled: artifactTabDisabled,
      icon: <FileTextOutlined />,
      label: resolvedUILabels.project_artifacts || '产物',
      value: 'project_artifacts',
    },
    {
      disabled: false,
      icon: <FolderOpenOutlined />,
      label: resolvedUILabels.project_sources || '资料库',
      value: 'sources',
    },
  ];
  return (
    <div aria-label="project collection tabs" className="flare-project-collection-tabs" role="tablist">
      {items.map((item) => (
        <button
          aria-selected={activeTab === item.value}
          className={['flare-project-collection-tab', activeTab === item.value ? 'is-active' : ''].filter(Boolean).join(' ')}
          disabled={item.disabled}
          key={item.value}
          onClick={() => onChange(item.value)}
          role="tab"
          type="button"
        >
          {item.icon}
          <span>{item.label}</span>
        </button>
      ))}
    </div>
  );
}

const artifactFactLabel = (artifact) => (
  [artifact.title, artifact.artifact_type, artifact.artifact_id]
    .map(value => String(value || '').trim())
    .find(Boolean) || ''
);
const buildSessionFacts = (artifactCollection) => {
  const items = Array.isArray(artifactCollection?.items) ? artifactCollection.items : [];
  return items.reduce((facts, artifactRecord) => {
    const artifact = Object(artifactRecord);
    const sessionId = String(artifact.source_session_id || '').trim();
    const label = artifactFactLabel(artifact);
    if (sessionId && label) {
      facts.set(sessionId, [...new Set([...(facts.get(sessionId) || []), label])]);
    }
    return facts;
  }, new Map());
};

function ConversationCollection({ artifactCollection, projectId, scrollElement, sessionCollection, themeTokens }) {
  const sessions = Array.isArray(sessionCollection.sessions) ? sessionCollection.sessions : [];
  const hasItems = sessions.length > 0;
  const sessionFactsById = buildSessionFacts(artifactCollection);
  return (
    <div className="flare-project-conversation-collection">
      <SessionRows
        activeSessionId={sessionCollection.activeSessionId}
        activeSessionRunning={sessionCollection.activeSessionRunning}
        emptySessionsLabel="暂无项目对话"
        hasMore={sessionCollection.hasMore}
        continuationError={sessionCollection.error}
        continuationKey={sessionCollection.continuationKey}
        loading={sessionCollection.loading && !hasItems}
        loadingMore={sessionCollection.loadingMore}
        loadingSessionId={sessionCollection.loadingSessionId}
        nested={false}
        onArchiveSession={sessionCollection.onArchiveSession}
        onLoadMore={sessionCollection.onLoadMore}
        onPinSession={sessionCollection.onPinSession}
        onRenameSession={sessionCollection.onRenameSession}
        onSelectSession={sessionCollection.onSelectSession}
        paneCompact={false}
        runningSessionIds={sessionCollection.runningSessionIds}
        scopeKey={`project-sessions:${projectId}:${sessionCollection.sortOrder}`}
        scrollElement={scrollElement}
        sessionFactsById={sessionFactsById}
        sessions={sessions}
        showFullTitle
        showGroups={false}
        showTimestamp
        themeTokens={themeTokens}
      />
    </div>
  );
}

function SessionSortSwitch({ onSort, sortOrder }) {
  return (
    <span aria-label="对话排序" className="flare-project-session-sort" role="group">
      {[['desc', '最新优先'], ['asc', '最早优先']].map(([value, label], index) => (
        <React.Fragment key={value}>
          {index ? <span aria-hidden="true" className="flare-project-session-sort-divider">|</span> : null}
          <button
            aria-pressed={sortOrder === value}
            className={sortOrder === value ? 'is-active' : ''}
            onClick={() => onSort?.(value)}
            type="button"
          >
            {label}
          </button>
        </React.Fragment>
      ))}
    </span>
  );
}

const resolveCollectionTab = ({ activeWorkspaceTab, artifactTabDisabled, projectId, tabSelection }) => {
  if (activeWorkspaceTab === 'sources') return 'sources';
  const selectedTab = tabSelection.projectId === projectId
    ? tabSelection.value : 'project_conversations';
  return artifactTabDisabled && selectedTab === 'project_artifacts'
    ? 'project_conversations' : selectedTab;
};
const collectionScrollProps = (activeTab, setScrollElement) => (
  activeTab === 'sources'
    ? { className: 'flare-project-collection-source-scroll' }
    : { className: 'flare-project-collection-list-scroll', ref: setScrollElement }
);

const projectCollectionViews = (search, sessionCollection, artifactCollection, sourcesPanelProps) => {
  const project = (kind, original) => search?.collection(kind, original) ?? original;
  const sessions = project('sessions', { ...sessionCollection, items: sessionCollection.sessions });
  const artifacts = project('artifacts', artifactCollection ?? {});
  const sources = project('sources', { items: sourcesPanelProps.sourceItems });
  return { sessions: { ...sessions, sessions: sessions.items }, artifacts, sources,
    sourcesPanel: { ...sourcesPanelProps, sourceItems: sources.items,
      sourceActionError: sources.error?.message ?? sourcesPanelProps.sourceActionError } };
};

const renderSourceCollection = collections => (
  <>
    <ChatWorkspaceSourcesPanel {...collections.sourcesPanel} composerNode={null} />
    {collections.sources.hasMore ? <Button type="link" onClick={collections.sources.onLoadMore}>加载更多</Button> : null}
  </>
);

const SEARCH_COLLECTIONS = [
  ['sessions', 'project_conversations', '项目对话'],
  ['artifacts', 'project_artifacts', '产物'],
  ['sources', 'sources', '资料库'],
];
const hasGroupedSearch = search => Boolean(search?.state.showResults
  && (search.state.acceptedQuery || search.state.error));

function GroupedProjectCollections({ search, collectionContent, labels, setScrollElement, children }) {
  if (!hasGroupedSearch(search)) return children;
  const { state } = search;
  const groups = new Map(state.groups.map(group => [group.kind, group]));
  const empty = state.status === 'ready'
    && state.groups.every(group => group.status === 'complete' && group.items.length === 0);
  return <div className="flare-project-collection-list-scroll flare-project-collection-search-groups" ref={setScrollElement}>
    {state.error ? <div role="alert">
      <Text type="danger">{state.error.message}</Text>
      <Button onClick={search.retry} type="link">重试</Button>
    </div> : null}
    {empty ? <Text type="secondary">没有匹配的标题</Text> : null}
    {SEARCH_COLLECTIONS.map(([kind, tab, fallback]) => {
      const group = groups.get(kind);
      if (!group || (group.status === 'complete' && group.items.length === 0)) return null;
      return <section data-search-kind={kind} key={kind}>
        <h3>{labels[tab === 'sources' ? 'project_sources' : tab] || fallback}</h3>
        {group.status !== 'complete' ? <div role="alert">
          <Text type="danger">搜索暂时不可用</Text>
          <Button onClick={search.retry} type="link">重试</Button>
        </div> : null}
        {group.items.length > 0 ? collectionContent.get(tab) : null}
      </section>;
    })}
  </div>;
}

function ProjectCollectionWorkspace({
  background,
  canvasPanelNode = null,
  canvasWorkspaceFullscreenMode = false,
  composerNode,
  isCompactLayout,
  mainContentNode,
  onCanvasPanelClose,
  showCanvasPanel = false,
}) {
  return (
    <ChatWorkspacePanelCoordinator
      canvasPanelNode={canvasWorkspaceFullscreenMode ? canvasPanelNode : null}
      canvasWorkspaceFullscreenMode={canvasWorkspaceFullscreenMode}
      composerNode={null}
      fullscreenComposerNode={null}
      isCompactLayout={isCompactLayout}
      showCanvasPanel={showCanvasPanel}
    >
      <ChatWorkspaceConversationLayout
        background={background}
        canvasPanelNode={canvasPanelNode}
        canvasWorkspaceFullscreenMode={canvasWorkspaceFullscreenMode}
        composerNode={composerNode}
        isCompactLayout={isCompactLayout}
        mainContentNode={mainContentNode}
        onCanvasPanelClose={onCanvasPanelClose}
        recommendationPanelNode={null}
        showCanvasPanel={showCanvasPanel}
      />
    </ChatWorkspacePanelCoordinator>
  );
}

export default function ProjectCollectionPane({
  activeWorkspaceTab = 'chats',
  projectSearch,
  onArchiveProject,
  onPinProject,
  onRenameProject,
  onWorkspaceTabChange,
  sourcesPanelProps = {},
  artifactCollection,
  canvasPanelNode,
  canvasWorkspaceFullscreenMode,
  onCanvasPanelClose,
  showCanvasPanel,
  composerNode,
  contentMaxWidth,
  isCompactLayout,
  project,
  resolvedUILabels,
  sessionCollection,
  themeTokens,
}) {
  const projectId = getProjectId(project);
  const labels = Object.assign({}, resolvedUILabels);
  const collections = projectCollectionViews(projectSearch, sessionCollection, artifactCollection, sourcesPanelProps);
  const [tabSelection, setTabSelection] = React.useState({
    projectId,
    value: 'project_conversations',
  });
  const [collectionScrollElement, setCollectionScrollElement] = React.useState(null);
  const artifactTabDisabled = !artifactCollection || artifactCollection.empty === true;
  const activeTab = resolveCollectionTab({
    activeWorkspaceTab, artifactTabDisabled, projectId, tabSelection,
  });
  const setActiveTab = (value) => {
    setTabSelection({ projectId, value });
    onWorkspaceTabChange?.(value === 'sources' ? 'sources' : 'chats');
  };
  React.useEffect(() => {
    if (activeTab !== tabSelection.value) {
      setTabSelection({ projectId, value: activeTab });
    }
  }, [activeTab, projectId, tabSelection.value]);
  const projectRecord = Object.assign({}, project);
  const itemCount = activeTab === 'project_artifacts'
    ? count(projectRecord.artifact_count)
    : count(projectRecord.session_count);
  const countLabel = activeTab === 'project_artifacts' ? '产物' : '对话';
  const collectionContent = new Map([
    ['project_conversations', <ConversationCollection artifactCollection={collections.artifacts} projectId={projectId} scrollElement={collectionScrollElement} sessionCollection={collections.sessions} themeTokens={themeTokens} />],
    ['project_artifacts', <ProjectArtifactCollection artifactCollection={collections.artifacts} projectId={projectId} scrollElement={collectionScrollElement} themeTokens={themeTokens} />],
    ['sources', renderSourceCollection(collections)],
  ]);
  const activeCollectionScrollProps = collectionScrollProps(activeTab, setCollectionScrollElement);
  const mainContentNode = (
    <div
      className="flare-project-collection-scroll"
      style={{ backgroundColor: themeTokens.appBg }}
    >
      <div className="flare-project-collection-content" style={{ maxWidth: contentMaxWidth }}>
        <div className="flare-project-detail-search">
          <ProjectSearchInput search={projectSearch} mode="detail" projectId={projectId} label="搜索当前项目标题" />
        </div>
        <GroupedProjectCollections search={projectSearch} collectionContent={collectionContent} labels={labels} setScrollElement={setCollectionScrollElement}>
        <div className="flare-project-collection-page" key={projectId}>
          <div className="flare-staged-entry" style={{ '--flare-entry-stage': 0 }}>
            <div className="flare-project-collection-toolbar">
              <ProjectCollectionTabs
                activeTab={activeTab}
                artifactTabDisabled={artifactTabDisabled}
                onChange={setActiveTab}
                resolvedUILabels={labels}
              />
              <ProjectDetailSettingsMenu
                onArchiveProject={onArchiveProject}
                onRenameProject={onRenameProject}
                onPinProject={onPinProject}
                project={project}
                themeTokens={themeTokens}
              />
            </div>
          </div>
          <div className="flare-project-collection-panel" key={activeTab}>
            {activeTab !== 'sources' ? (
              <div className="flare-project-collection-count flare-staged-entry" style={{ '--flare-entry-stage': 1 }}>
                <Text type="secondary">共{itemCount}个{countLabel}</Text>
                {activeTab === 'project_conversations' ? (
                  <SessionSortSwitch
                    onSort={sessionCollection.onSortSessions}
                    sortOrder={sessionCollection.sortOrder}
                  />
                ) : null}
              </div>
            ) : null}
            <div {...activeCollectionScrollProps}>
              <div className="flare-staged-entry" style={{ '--flare-entry-stage': 2 }}>
                {collectionContent.get(activeTab)}
              </div>
            </div>
          </div>
        </div>
        </GroupedProjectCollections>
      </div>
    </div>
  );

  return (
    <ProjectCollectionWorkspace
      background={themeTokens.appBg}
      canvasPanelNode={canvasPanelNode}
      canvasWorkspaceFullscreenMode={canvasWorkspaceFullscreenMode}
      composerNode={composerNode}
      isCompactLayout={isCompactLayout}
      mainContentNode={mainContentNode}
      onCanvasPanelClose={onCanvasPanelClose}
      showCanvasPanel={showCanvasPanel}
    />
  );
}
